import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { fetchAchievements } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { celebrate } from "@/lib/achievements";
import { resolveAchievementTitle, resolveAchievementDesc } from "@/lib/i18nContent";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Sprout, Scroll, Book, Flame, Trophy, RefreshCcw, Crown, Star,
  Heart, Lightbulb, Lock, Award, Share2, Download, Copy,
  Facebook, Instagram, X as XIcon,
} from "lucide-react";

const ICONS = {
  sprout: Sprout,
  scroll: Scroll,
  book: Book,
  flame: Flame,
  trophy: Trophy,
  refresh: RefreshCcw,
  crown: Crown,
  star: Star,
  heart: Heart,
  lightbulb: Lightbulb,
};

function BadgeCard({ a, onShare }) {
  const { t } = useI18n();
  const Icon = ICONS[a.icon] || Award;
  const title = resolveAchievementTitle(t, a);
  const desc = resolveAchievementDesc(t, a);
  return (
    <div
      data-testid={`achievement-${a.id}`}
      className={`rounded-2xl bg-white border border-[#E7E0D2] p-5 transition ${
        a.earned ? "" : "opacity-70"
      }`}
    >
      <div className="flex items-start justify-between">
        <div
          className={`h-12 w-12 rounded-xl grid place-items-center ${
            a.earned
              ? "bg-[#FEF3C7] text-[#D97706]"
              : "bg-[#F3F4F6] text-[#9CA3AF]"
          }`}
        >
          {a.earned ? <Icon className="h-6 w-6" /> : <Lock className="h-5 w-5" />}
        </div>
        {a.earned && onShare && (
          <button
            data-testid={`achievement-share-${a.id}`}
            onClick={() => onShare({ ...a, title, desc })}
            title={t("achievements.share_tooltip")}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#FFF7ED] border border-[#FED7AA] text-[#B45309] hover:border-[#D97706] transition">
            <Share2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="mt-4 font-heading font-semibold text-[#1F2937]">{title}</div>
      <p className="mt-1 text-xs text-[#4B5563] leading-relaxed">{desc}</p>

      {a.earned ? (
        <div className="mt-3 text-[11px] font-semibold text-[#D97706]">
          {a.earned_at
            ? t("achievements.earned_on", { date: new Date(a.earned_at).toLocaleDateString() })
            : t("achievements.earned_no_date")}
        </div>
      ) : (
        <div className="mt-3">
          <div className="h-1.5 rounded-full bg-[#E7E0D2] overflow-hidden">
            <div
              className="h-full bg-[#064E3B]/60 transition-all duration-700"
              style={{ width: `${a.progress}%` }}
            />
          </div>
          {a.progress_label && (
            <div className="mt-1 text-[10px] text-[#4B5563]">{a.progress_label}</div>
          )}
        </div>
      )}
    </div>
  );
}

function BadgeSection({ testId, label, badges, onShare }) {
  if (!badges.length) return null;
  const earned = badges.filter((a) => a.earned).length;
  return (
    <section className="mt-8">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] font-semibold">{label}</div>
        <div className="text-xs text-[#4B5563]">{earned}/{badges.length}</div>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={testId}>
        {badges.map((a) => <BadgeCard key={a.id} a={a} onShare={onShare} />)}
      </div>
    </section>
  );
}

export default function Achievements() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [showModuleBadges, setShowModuleBadges] = useState(false);
  const [sharing, setSharing] = useState(null); // badge currently open in the share modal

  useEffect(() => {
    fetchAchievements()
      .then((data) => {
        setItems(data);
        if (data.some((a) => a.new)) celebrate();
      })
      .catch(() => setItems([]));
  }, []);

  const earned = (items || []).filter((a) => a.earned);
  const moduleBadges = (items || []).filter((a) => a.category === "module");
  const milestoneBadges = (items || []).filter((a) => a.category !== "module");

  return (
    <div className="min-h-screen flex flex-col" data-testid="achievements-page">
      <Header />
      <main className="flex-1 max-w-[896px] mx-auto w-full px-5 sm:px-8 py-10">
        <header>
          <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-semibold">{t("achievements.motivation_eyebrow")}</div>
          <h1 className="mt-1 font-heading font-semibold text-4xl text-[#1F2937]">{t("achievements.title")}</h1>
          <p className="mt-2 text-[#4B5563]">{t("achievements.subtitle")}</p>
        </header>

        {items && (
          <div className="mt-2 text-sm text-[#4B5563]">
            {t("achievements.badges_earned", { earned: earned.length, total: items.length })}
          </div>
        )}

        {!items ? (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-[#F5EFE3]/50 border border-[#E7E0D2] h-44 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <BadgeSection testId="milestone-badges-grid" label={t("achievements.milestones_label")} badges={milestoneBadges} onShare={setSharing} />

            {moduleBadges.length > 0 && (
              <div className="mt-8">
                <button
                  data-testid="module-badges-toggle"
                  onClick={() => setShowModuleBadges((v) => !v)}
                  className="text-xs uppercase tracking-[0.22em] text-[#4B5563] font-semibold hover:text-[#1F2937] transition">
                  {showModuleBadges ? t("achievements.hide_module_badges") : t("achievements.show_module_badges")}
                </button>
                {showModuleBadges && <BadgeSection testId="module-badges-grid" label={t("achievements.module_badges_label")} badges={moduleBadges} onShare={setSharing} />}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
      {sharing && <ShareModal badge={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}

/**
 * Per-badge "Share & inspire" modal — a downloadable/shareable image (badge
 * name + description + QR code + CTA) plus quick links to the major social
 * channels. Instagram has no web share-intent URL, so its button downloads
 * the image and hints at posting it manually to a story or feed.
 */
function ShareModal({ badge, onClose }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const qrRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  // Fixed marketing domain — the QR always points here regardless of which
  // host (localhost, staging, app subdomain) the badge was shared from.
  const siteUrl = "https://qfc.hikmahverses.com";
  const caption = `${badge.title} — ${badge.desc} Test your Quran fluency free at ${siteUrl}`;

  const buildImage = () => {
    const canvas = canvasRef.current;
    const qr = qrRef.current;
    if (!canvas || !qr) return null;
    const ctx = canvas.getContext("2d");
    const W = 800, H = 1000;
    canvas.width = W; canvas.height = H;

    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, "#064E3B");
    grad.addColorStop(1, "#0B3B2E");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = "#FED7AA";
    ctx.font = "bold 28px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("QURAN FLUENCY CERTIFICATION", W / 2, 90);

    ctx.beginPath();
    ctx.arc(W / 2, 300, 130, 0, Math.PI * 2);
    ctx.fillStyle = "#D97706";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(W / 2, 300, 110, 0, Math.PI * 2);
    ctx.strokeStyle = "#FED7AA";
    ctx.lineWidth = 4;
    ctx.stroke();

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 90px sans-serif";
    ctx.fillText("★", W / 2, 335);

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 44px sans-serif";
    ctx.fillText(badge.title, W / 2, 500);

    ctx.fillStyle = "#FED7AA";
    ctx.font = "22px sans-serif";
    wrapText(ctx, badge.desc, W / 2, 545, 600, 30);

    // ~86% of the heading's 44px size, bold — visually prominent without
    // outsizing the "QFC Self-Assessment Completed" heading above it.
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 38px sans-serif";
    ctx.fillText(user?.name || "QFC Student", W / 2, 645);

    const qrSize = 180;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(W / 2 - qrSize / 2 - 12, 710, qrSize + 24, qrSize + 24);
    ctx.drawImage(qr, W / 2 - qrSize / 2, 722, qrSize, qrSize);

    ctx.fillStyle = "#FED7AA";
    ctx.font = "bold 24px sans-serif";
    ctx.fillText("Test your fluency — scan to try QFC", W / 2, 950);

    return canvas;
  };

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const canvas = buildImage();
      if (canvas) setImgUrl(canvas.toDataURL("image/png"));
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadImage = () => {
    const canvas = buildImage();
    if (!canvas) return;
    const a = document.createElement("a");
    a.download = `qfc-${badge.id}-badge.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  const openInstagram = () => {
    downloadImage();
    toast.message("Image downloaded — open Instagram and share it to your story or feed.");
  };
  const openWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener");
  const openX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`, "_blank", "noopener");
  const openFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(caption)}`, "_blank", "noopener");
  const copyText = async () => {
    try { await navigator.clipboard.writeText(caption); toast.success(t("achievements.toast_copied")); }
    catch { toast.error(t("achievements.toast_copy_failed")); }
  };
  const nativeShare = async () => {
    const canvas = buildImage();
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `qfc-${badge.id}-badge.png`, { type: "image/png" });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        try { await navigator.share({ files: [file], title: badge.title, text: caption }); return; } catch { /* cancelled */ }
      } else if (navigator.share) {
        try { await navigator.share({ title: badge.title, text: caption, url: siteUrl }); return; } catch { /* cancelled */ }
      }
      downloadImage();
    }, "image/png");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal-backdrop">
      <div className="bg-white rounded-3xl max-w-sm w-full overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <div className="p-5 border-b border-[#E7E0D2] flex items-center justify-between">
          <div className="font-heading font-semibold text-[#1F2937]">{t("achievements.share_tooltip")}</div>
          <button onClick={onClose} data-testid="share-modal-close" className="text-[#4B5563] hover:text-[#1F2937] text-lg leading-none">✕</button>
        </div>
        <div className="p-5">
          {imgUrl ? (
            <img src={imgUrl} alt={badge.title} className="w-full rounded-2xl" />
          ) : (
            <div className="w-full aspect-[4/5] rounded-2xl bg-[#064E3B]/10 animate-pulse" />
          )}
          {/* Row 1 — social platforms, each in its own brand color so they
              read as recognizable share targets at a glance. */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            <SocialShareBtn label="WhatsApp" icon={WhatsAppIcon} bgClass="bg-[#25D366] hover:bg-[#1DA851]" onClick={openWhatsApp} />
            <SocialShareBtn label="Instagram" icon={Instagram} bgClass="bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90" onClick={openInstagram} />
            <SocialShareBtn label="X" icon={XIcon} bgClass="bg-black hover:bg-[#1a1a1a]" onClick={openX} />
            <SocialShareBtn label="Facebook" icon={Facebook} bgClass="bg-[#1877F2] hover:bg-[#0f63d1]" onClick={openFacebook} />
          </div>

          {/* Row 2 — neutral utility actions, visually secondary to the
              social row above. */}
          <div className="mt-2.5 flex gap-2">
            <ShareBtn label="Download Image" icon={Download} onClick={downloadImage} />
            <ShareBtn label="Copy Text" icon={Copy} onClick={copyText} />
            {typeof navigator.share === "function" && <ShareBtn label="More" icon={Share2} onClick={nativeShare} />}
          </div>

          <p className="mt-3 text-[11px] text-[#4B5563] text-center">{t("achievements.instagram_helper")}</p>
        </div>
      </div>
      {/* Offscreen: source QR (canvas) — used to build the composited image */}
      <div className="hidden">
        <QRCodeCanvas ref={qrRef} value={siteUrl} size={360} level="M" marginSize={1} />
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}

// lucide-react ships Facebook/Instagram/X icons already, but no WhatsApp
// mark — this is the one glyph the project's existing icon library doesn't
// cover, so a small inline SVG fills the gap (no new dependency), sized and
// colored (currentColor) to match the lucide icons beside it.
function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
    </svg>
  );
}

// Row 1 — social platforms. Same click-handler contract as ShareBtn below,
// just styled prominently in each platform's own brand color instead of the
// app's neutral secondary-button treatment.
function SocialShareBtn({ label, icon: Icon, bgClass, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`share-btn-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex flex-col items-center justify-center gap-1 py-3 rounded-2xl text-white text-[11px] font-medium shadow-sm transition active:scale-[0.98] ${bgClass}`}>
      <Icon className="w-5 h-5" />
      {label}
    </button>
  );
}

// Row 2 — neutral utility actions (download / copy / native share), kept
// visually secondary to the social row above.
function ShareBtn({ label, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`share-btn-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex-1 flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[10px] text-[#1F2937] transition">
      {Icon ? <Icon className="w-4 h-4" /> : <span className="w-4 h-4 grid place-items-center text-xs font-bold">{label[0]}</span>}
      {label}
    </button>
  );
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  let lineY = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, lineY);
      line = word;
      lineY += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, lineY);
}
